import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, ChevronLeft, ChevronRight, RefreshCw, Play, Loader2 } from 'lucide-react';

const DeveloperTranscripts = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [transcripts, setTranscripts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [counts, setCounts] = useState({ total: 0, processed: 0, pending: 0 });
  const perPage = 50;

  useEffect(() => {
    if (!organizationId) return;
    loadTranscripts();
    loadCounts();
  }, [organizationId, page]);

  const loadCounts = async () => {
    const [totalRes, procRes] = await Promise.all([
      supabase.from('sessions').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId).not('raw_transcript', 'is', null),
      supabase.from('sessions').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId).not('raw_transcript', 'is', null).eq('is_processed_v2', true),
    ]);
    const total = totalRes.count || 0;
    const processed = procRes.count || 0;
    setCounts({ total, processed, pending: total - processed });
  };

  const loadTranscripts = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('sessions')
        .select('id, session_id, tutor_name, student_name, lesson_timestamp, duration_minutes, is_processed_v2, processed_at, synced_at')
        .eq('organization_id', organizationId)
        .not('raw_transcript', 'is', null)
        .order('lesson_timestamp', { ascending: false })
        .range(page * perPage, (page + 1) * perPage - 1);
      setTranscripts(data || []);
    } catch (err) {
      console.error('Error loading transcripts:', err);
    }
    setLoading(false);
  };

  const loadTranscriptText = async (id) => {
    const { data } = await supabase.from('sessions').select('raw_transcript').eq('id', id).single();
    return data?.raw_transcript || '';
  };

  const downloadTxt = async (t) => {
    const text = await loadTranscriptText(t.id);
    const header = `Tutor: ${t.tutor_name}\nStudent: ${t.student_name}\nDate: ${t.lesson_timestamp}\n\n`;
    const blob = new Blob([header + text], { type: 'text/plain' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `transcript-${t.tutor_name}-${t.student_name}.txt`; a.click();
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const totalPages = Math.ceil(counts.total / perPage);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/developer')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to Developer
      </button>

      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Transcripts
        </h1>
        <button onClick={() => { loadTranscripts(); loadCounts(); }} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-[#18181b] text-[#a1a1aa]' : 'hover:bg-[#f0f0f0] text-[#666]'}`}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total', value: counts.total },
          { label: 'Processed', value: counts.processed },
          { label: 'Pending', value: counts.pending },
        ].map(s => (
          <div key={s.label} className={`${cardClass} p-4`}>
            <span className={`text-xs block ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{s.label}</span>
            <span className={`text-xl font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* Two-panel */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* List */}
        <div className={`${cardClass} lg:col-span-2 overflow-hidden`}>
          <div className={`max-h-[600px] overflow-y-auto divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
            {loading ? (
              <div className={`p-8 text-center text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Loading...</div>
            ) : transcripts.map(t => (
              <button
                key={t.id}
                onClick={() => setSelected(t)}
                className={`w-full px-4 py-3 text-left transition-colors ${
                  selected?.id === t.id
                    ? isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'
                    : isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{t.tutor_name} → {t.student_name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    t.is_processed_v2
                      ? 'bg-emerald-400/10 text-emerald-400'
                      : 'bg-amber-400/10 text-amber-400'
                  }`}>
                    {t.is_processed_v2 ? 'Processed' : 'Pending'}
                  </span>
                </div>
                <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  {t.duration_minutes}m · {t.lesson_timestamp ? new Date(t.lesson_timestamp).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '--'}
                </div>
              </button>
            ))}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`flex items-center justify-center gap-2 p-3 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="p-1 disabled:opacity-30">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{page + 1} / {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="p-1 disabled:opacity-30">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Detail */}
        <div className={`${cardClass} lg:col-span-3 p-5`}>
          {!selected ? (
            <div className={`text-center py-16 text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Select a transcript to view</div>
          ) : (
            <TranscriptDetail session={selected} isDark={isDark} onDownload={() => downloadTxt(selected)} loadText={loadTranscriptText} />
          )}
        </div>
      </div>
    </div>
  );
};

const TranscriptDetail = ({ session, isDark, onDownload, loadText }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [reprocessing, setReprocessing] = useState(false);

  useEffect(() => {
    setLoading(true);
    loadText(session.id).then(t => { setText(t); setLoading(false); });
  }, [session.id]);

  const handleReprocess = async () => {
    const code = prompt('Enter developer code to reprocess:');
    if (code !== '240501') return;
    setReprocessing(true);
    try {
      await supabase.functions.invoke('process-transcript-full', { body: { sessionId: session.id } });
    } catch (err) {
      console.error('Reprocess error:', err);
    }
    setReprocessing(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{session.tutor_name} → {session.student_name}</h3>
        <div className="flex items-center gap-1">
          <button onClick={handleReprocess} disabled={reprocessing}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#18181b] text-[#52525b]' : 'hover:bg-[#f0f0f0] text-[#999]'}`}
            title="Reprocess">
            {reprocessing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <button onClick={onDownload} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#18181b] text-[#52525b]' : 'hover:bg-[#f0f0f0] text-[#999]'}`}>
            <Download className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className={`text-xs space-y-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
        <div>Duration: {session.duration_minutes}m</div>
        <div>Date: {session.lesson_timestamp ? new Date(session.lesson_timestamp).toLocaleString() : '--'}</div>
        <div>Synced: {session.synced_at ? new Date(session.synced_at).toLocaleString() : '--'}</div>
        {session.processed_at && <div>Processed: {new Date(session.processed_at).toLocaleString()}</div>}
      </div>
      <div className={`border-t pt-3 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
        {loading ? (
          <div className={`animate-pulse text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Loading transcript...</div>
        ) : (
          <pre className={`text-xs leading-relaxed whitespace-pre-wrap max-h-[400px] overflow-y-auto ${isDark ? 'text-[#a1a1aa]' : 'text-[#555]'}`}>
            {text || 'No transcript text available'}
          </pre>
        )}
      </div>
    </div>
  );
};

export default DeveloperTranscripts;
